import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";
import { Surface } from "./Surface";
import { Text } from "./Text";

/**
 * Centered modal that nudges the user to upgrade — the single shared surface for
 * the global `402 quota_exceeded` handler and any plan-gated CTA. Presentational
 * and i18n-free: the caller passes already-translated copy and wires the
 * actions. Self-contained (portal + backdrop + Escape + focus), monochrome.
 */
export interface UpgradeModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  /** Primary CTA label (e.g. "See plans"). */
  primaryLabel: string;
  onPrimary: () => void;
  /** Dismiss label (e.g. "Not now"). */
  secondaryLabel: string;
  /** Accessible label for the backdrop dismiss button. */
  closeLabel: string;
}

export function UpgradeModal({
  open,
  onClose,
  title,
  description,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  closeLabel,
}: UpgradeModalProps) {
  const primaryRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    primaryRef.current?.focus();
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-modal-title"
    >
      <button
        type="button"
        aria-label={closeLabel}
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-ink/30 backdrop-blur-[2px]"
      />

      <Surface
        tone="paper"
        radius="lg"
        border="hairline"
        shadow="pill"
        className="relative flex w-full max-w-[420px] flex-col items-start gap-4 p-7 text-left"
      >
        <span
          aria-hidden="true"
          className="grid size-10 place-items-center rounded-md bg-ink text-paper"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M13 2 4.5 13.5H11l-1 8.5L19.5 10H13z" />
          </svg>
        </span>

        <div className="flex flex-col gap-2">
          <Text variant="h4" as="h2" id="upgrade-modal-title">
            {title}
          </Text>
          <Text variant="body" className="text-ink-soft">
            {description}
          </Text>
        </div>

        <div className="mt-1 flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="subtle" size="md" onClick={onClose}>
            {secondaryLabel}
          </Button>
          <Button ref={primaryRef} variant="accent" size="md" onClick={onPrimary}>
            {primaryLabel}
          </Button>
        </div>
      </Surface>
    </div>,
    document.body,
  );
}
